import type { Localized } from "@/i18n/types";

export type Project = {
  id: string;
  title: Localized;
  org: string;
  role?: Localized;
  summary: Localized;
  detail: Localized;
  tags: readonly string[];
};

/**
 * Six case studies, ordered by how distinctive they are rather than
 * chronologically — the hardware/ML one leads because nothing else on the
 * resume looks like it.
 */
export const projects: readonly Project[] = [
  {
    id: "ticket-scanner",
    title: {
      en: "Ticket Scanner: hardware, OCR and a state machine",
      pt: "Ticket Scanner: hardware, OCR e uma máquina de estados",
    },
    org: "Lottery Now",
    summary: {
      en: "Physical lottery tickets had to be read, reconciled against draw results, and sorted by hand.",
      pt: "Bilhetes físicos de loteria precisavam ser lidos, conferidos contra os resultados dos sorteios e separados à mão.",
    },
    detail: {
      en: "Built an Electron + React desktop app driving the scanner hardware through its vendor DLL via Koffi FFI, with XState modelling the scan → OCR → reconcile → route cycle so no ticket can land in an undefined state. YOLO for region detection and TrOCR for character recognition, trained and served on SageMaker; results persisted to Postgres. Winning and losing tickets now route automatically into separate hardware slots.",
      pt: "Construí um app desktop em Electron + React que controla o hardware do scanner pela DLL do fabricante via Koffi FFI, com XState modelando o ciclo leitura → OCR → conferência → roteamento, de modo que nenhum bilhete termine em um estado indefinido. YOLO para detecção de regiões e TrOCR para reconhecimento de caracteres, treinados e servidos no SageMaker; resultados persistidos no Postgres. Bilhetes premiados e não premiados passaram a ser encaminhados automaticamente para compartimentos separados.",
    },
    tags: ["Electron", "React", "XState", "Koffi/FFI", "YOLO", "TrOCR", "SageMaker", "PostgreSQL"],
  },
  {
    id: "mido-lotto",
    title: {
      en: "Mido Lotto: purchase flows under regulation",
      pt: "Mido Lotto: fluxos de compra sob regulação",
    },
    org: "Lottery Now",
    role: { en: "Lead engineer", pt: "Engenheiro líder" },
    summary: {
      en: "A consumer PWA selling lottery tickets, bets and scratchers across jurisdictions with different rules.",
      pt: "Um PWA de consumo vendendo bilhetes de loteria, apostas e raspadinhas em jurisdições com regras diferentes.",
    },
    detail: {
      en: "Next.js/TypeScript PWA covering tickets, bets and scratchers. Purchase and compliance flows have a lot of legally distinct states, so they're modelled in XState rather than ad-hoc booleans. GeoComply for jurisdiction checks, Firebase Remote Config for rollout, Segment/Customer.io/AppsFlyer for instrumentation. Added E2E coverage to the release path.",
      pt: "PWA em Next.js/TypeScript cobrindo bilhetes, apostas e raspadinhas. Os fluxos de compra e conformidade têm muitos estados juridicamente distintos, então foram modelados em XState em vez de booleanos improvisados. GeoComply para verificação de jurisdição, Firebase Remote Config para rollout e Segment/Customer.io/AppsFlyer para instrumentação. Adicionei cobertura E2E ao processo de release.",
    },
    tags: [
      "Next.js",
      "TypeScript",
      "XState",
      "Tailwind",
      "styled-components",
      "GeoComply",
      "Firebase",
      "PWA",
    ],
  },
  {
    id: "web-wallet",
    title: {
      en: "Web Wallet: four payment processors, one flow",
      pt: "Web Wallet: quatro processadores de pagamento, um só fluxo",
    },
    org: "Lottery Now",
    summary: {
      en: "Deposits and withdrawals had to work across four processors without exposing that split to the user.",
      pt: "Depósitos e saques precisavam funcionar em quatro processadores sem expor essa divisão ao usuário.",
    },
    detail: {
      en: "React + Redux-Saga wallet integrating PayNearMe, Venmo, SeamlessChex and Trustly behind a single deposit/withdraw experience. Owned monitoring, incident response and feature delivery — when the product moves real money, the person who built it carries the pager.",
      pt: "Carteira em React + Redux-Saga integrando PayNearMe, Venmo, SeamlessChex e Trustly sob uma única experiência de depósito e saque. Fui responsável por monitoramento, resposta a incidentes e entrega de features — quando o produto movimenta dinheiro real, quem construiu carrega o pager.",
    },
    tags: ["React", "Redux-Saga", "PayNearMe", "Venmo", "Trustly", "SeamlessChex"],
  },
  {
    id: "realtime-bff",
    title: {
      en: "Real-time BFF on AWS",
      pt: "BFF em tempo real na AWS",
    },
    org: "Lottery Now",
    summary: {
      en: "Client apps needed one coherent API over a spread of microservices, with live updates and spiky draw-night load.",
      pt: "Os apps clientes precisavam de uma API coerente sobre vários microsserviços, com atualizações ao vivo e picos de carga em noite de sorteio.",
    },
    detail: {
      en: "NestJS + TypeScript + GraphQL + Postgres service fronting multiple microservices, with SQS/SNS carrying real-time messages and the whole thing built to scale elastically under load. Shipped with Terraform + GitHub Actions to ECS, RDS, S3, Lambda, API Gateway, Route 53, CloudFront, SQS and SNS, plus pre-release integration tests as a server sanity gate.",
      pt: "Serviço em NestJS + TypeScript + GraphQL + Postgres na frente de múltiplos microsserviços, com SQS/SNS carregando mensagens em tempo real e todo o conjunto construído para escalar de forma elástica sob carga. Entregue com Terraform + GitHub Actions para ECS, RDS, S3, Lambda, API Gateway, Route 53, CloudFront, SQS e SNS, além de testes de integração pré-release como validação do servidor.",
    },
    tags: ["NestJS", "GraphQL", "PostgreSQL", "SQS/SNS", "ECS", "Terraform", "GitHub Actions"],
  },
  {
    id: "cpa-ontario",
    title: {
      en: "CPA Ontario: legacy C# to Next.js on Azure",
      pt: "CPA Ontario: de C# legado para Next.js no Azure",
    },
    org: "DevelopPulse",
    role: { en: "Lead", pt: "Líder técnico" },
    summary: {
      en: "A professional body's site needed to move off C# to a modern SSR stack on an aggressive timeline.",
      pt: "O site de um conselho profissional precisava sair do C# para uma stack SSR moderna em um prazo agressivo.",
    },
    detail: {
      en: "Refactored a C# + React site to Next.js/TypeScript SSR on Agility CMS, containerised on Azure. The hard bug: every container redeploy wiped prerendered content — fixed by persisting Next.js build artifacts on external volumes. Also untangled Cloudflare edge routing and caching, and used Cloudflare Workers for proxy and CDN caching. Delivered on time; bonus awarded.",
      pt: "Refatorei um site em C# + React para Next.js/TypeScript com SSR sobre o Agility CMS, containerizado no Azure. O bug difícil: cada redeploy de container apagava o conteúdo pré-renderizado — resolvido persistindo os artefatos de build do Next.js em volumes externos. Também desembaracei o roteamento e o cache de borda no Cloudflare, e usei Cloudflare Workers para proxy e cache de CDN. Entregue no prazo, com bônus concedido.",
    },
    tags: ["Next.js SSR", "TypeScript", "Agility CMS", "Docker", "Azure", "Cloudflare Workers"],
  },
  {
    id: "embroker",
    title: {
      en: "Embroker: client platform + micro-frontend admin",
      pt: "Embroker: plataforma do cliente + admin em micro-frontend",
    },
    org: "Nexton",
    role: { en: "Client engagement", pt: "Alocação em cliente" },
    summary: {
      en: "Two insurance platforms — a mobile-first client app and an admin console split across two frameworks.",
      pt: "Duas plataformas de seguros — um app cliente mobile-first e um console administrativo dividido entre dois frameworks.",
    },
    detail: {
      en: "Mobile-first React + TypeScript + React Query client platform — new features, RCA on cross-layer bugs, dependency and vulnerability remediation. On the admin side, a micro-frontend pairing an Angular shell with React modules: refactored legacy logic and stabilised the integration layers. Built the Jest / RTL / Cypress suites and wired them in as CI gates on PRs and releases.",
      pt: "Plataforma cliente mobile-first em React + TypeScript + React Query — novas features, análise de causa raiz em bugs entre camadas e correção de dependências e vulnerabilidades. No admin, um micro-frontend combinando um shell Angular com módulos React: refatorei lógica legada e estabilizei as camadas de integração. Construí as suítes Jest / RTL / Cypress e as integrei como barreiras de CI em PRs e releases.",
    },
    tags: ["React", "TypeScript", "React Query", "Angular shell", "Micro-frontends", "Cypress"],
  },
];

export type AlsoShipped = { name: string; note: Localized };

export const alsoShipped: readonly AlsoShipped[] = [
  {
    name: "The King Center",
    note: {
      en: "Next.js SSR on headless WordPress, with Microsoft",
      pt: "Next.js com SSR sobre WordPress headless, junto à Microsoft",
    },
  },
  {
    name: "Computer History Museum",
    note: {
      en: "Catalog with selective SSR and dynamic filtering",
      pt: "Catálogo com SSR seletivo e filtros dinâmicos",
    },
  },
  {
    name: "Stroll",
    note: {
      en: "Ionic/Capacitor GPS tracking, shipped iOS + Android",
      pt: "Rastreamento por GPS em Ionic/Capacitor, publicado em iOS e Android",
    },
  },
  {
    name: "Dapix",
    note: {
      en: "Crypto wallet in React Native, stabilised async transaction state",
      pt: "Carteira cripto em React Native, com estabilização do estado assíncrono das transações",
    },
  },
  {
    name: "Philips Tasy",
    note: {
      en: "Healthcare platform; mentored ~15 devs through the migration",
      pt: "Plataforma de saúde; mentorei ~15 desenvolvedores durante a migração",
    },
  },
  {
    name: "Bunge",
    note: {
      en: "JSON-driven ViewEngine for SAP-integrated forms",
      pt: "ViewEngine orientada a JSON para formulários integrados ao SAP",
    },
  },
  {
    name: "Linx POS",
    note: {
      en: "Electron gas-station POS with thermal printers and card readers",
      pt: "PDV de posto de combustível em Electron com impressoras térmicas e leitores de cartão",
    },
  },
  {
    name: "WCRA",
    note: {
      en: "Admin + player apps on React/NestJS, led the TypeScript refactor",
      pt: "Apps de admin e jogador em React/NestJS; liderei a refatoração para TypeScript",
    },
  },
];
